import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/survey-core.min.css';
import surveyTheme from '../surveyTheme';
import api from '../api/api';

export default function PublicSurveyPage() {
    const { id } = useParams();
    const [survey, setSurvey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        api.get(`/survey/public/${id}`)
            .then(res => setSurvey(res.data.survey))
            .catch(err => {
                if (err.response?.status === 404) {
                    setError('This survey is not available.');
                } else {
                    setError('Failed to load survey. Please try again later.');
                }
            })
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-white">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-white">
                <p className="text-slate-500 text-lg">{error}</p>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-white">
                <div className="text-center max-w-md px-6">
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Thank you!</h2>
                    <p className="text-slate-500">Your response has been submitted.</p>
                </div>
            </div>
        );
    }

    return <SurveyContent survey={survey} surveyId={id} onSubmit={() => setSubmitted(true)} />;
}

function SurveyContent({ survey, surveyId, onSubmit }) {
    // The thank-you screen only shows once the response is saved; a failed send
    // keeps the answers so the visitor can send them again
    const [sending, setSending] = useState(false);
    const [failed, setFailed] = useState(null); // { data, message, closed }
    const send = async (data) => {
        setSending(true);
        setFailed(null);
        try {
            await api.post(`/survey/public/${surveyId}/respond`, { response_data: data });
            onSubmit();
        } catch (err) {
            console.error('Survey submit error:', err);
            const closed = err.response?.status === 404;
            setFailed({
                data,
                closed,
                message: closed
                    ? 'This survey is closed, so your response could not be sent.'
                    : 'Your response could not be sent. Please try again.',
            });
        } finally {
            setSending(false);
        }
    };
    const [model] = useState(() => {
        const m = new Model(survey.SurveyJSON);
        // Brand theme: white shadowed cards + emerald palette, matching the
        // rest of the app (public pages are always light per App.js)
        m.applyTheme(surveyTheme);
        // Force consistent full-width rendering — the default "auto" mode
        // picks static (narrow, centered) or responsive per question type,
        // making different surveys render at different widths.
        m.widthMode = "responsive";
        // Our own thank-you screen shows after a successful send (below)
        m.showCompletedPage = false;
        m.onComplete.add(async (sender) => {
            //try {
            //    await api.post(`/survey/public/${surveyId}/respond`, {
            //        response_data: sender.data,
            //    });
            //} catch (err) {
            //    console.error('Survey submit error:', err);
            //} finally {
            //    onSubmit();
            //}
            await send(sender.data);
        });
        return m;
    });

    return (
        <div className="min-h-screen bg-slate-50">
            {sending && (
                <p className="max-w-2xl mx-auto px-6 pt-10 text-center text-slate-500">Sending your response…</p>
            )}
            {failed && (
                <div role="alert" className="max-w-2xl mx-auto px-6 pt-10">
                    <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-red-700 flex flex-wrap items-center justify-between gap-3">
                        <span>{failed.message}</span>
                        {!failed.closed && (
                            <button
                                type="button"
                                onClick={() => send(failed.data)}
                                className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700"
                            >
                                Try again
                            </button>
                        )}
                    </div>
                </div>
            )}
            <Survey model={model} />
        </div>
    );
}
